<template>
  <div class="offer-list-wrapper">
    <div v-for="group in groupedOffers" :key="group.mode" class="offer-group">
      <div class="offer-group-header" :class="group.mode === 'EXCLUDE' ? 'mode-exclude' : 'mode-include'">
        <VdIcon store="fas" :name="group.mode === 'EXCLUDE' ? 'ban' : 'circle-check'" custom-size="12px" />
        {{ group.label }}
      </div>
      <CampaignOfferGroup
        v-for="campaignGroup in group.campaigns"
        :key="campaignGroup.campaignId"
        :label="campaignGroup.label"
        :offers="campaignGroup.offers"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { TreatmentOfferSummary } from "@/modules/Offers/hooks/useTreatmentOffersData";

import CampaignOfferGroup from "./CampaignOfferGroup.vue";

// Purely presentational: one instance renders per cohort × treatment, so the offers/campaigns data is
// fetched once at page level (useTreatmentOffersData) and passed down instead of fetched here.
const props = defineProps<{
  offerIds: string[];
  offersById: Record<string, TreatmentOfferSummary>;
  campaignNamesById: Record<string, string>;
}>();

const getOfferMode = (offerId: string) => props.offersById[offerId]?.abtestLinkMode ?? null;

// Group a set of offer ids by campaign (named campaigns first, alphabetically; "No campaign" last).
const NO_CAMPAIGN = "__none__";
const groupByCampaign = (offerIds: string[]) => {
  const byCampaign = new Map<string, { id: string; name: string }[]>();
  offerIds.forEach((id) => {
    const offer = props.offersById[id];
    const key = offer?.campaignId ?? NO_CAMPAIGN;
    if (!byCampaign.has(key)) {
      byCampaign.set(key, []);
    }
    byCampaign.get(key)?.push({ id, name: offer?.name ?? id });
  });
  return [...byCampaign.entries()]
    .map(([campaignId, offers]) => ({
      campaignId,
      label: campaignId === NO_CAMPAIGN ? "No campaign" : props.campaignNamesById[campaignId] ?? campaignId,
      offers,
    }))
    .sort((a, b) => {
      if (a.campaignId === NO_CAMPAIGN) {
        return 1;
      }
      if (b.campaignId === NO_CAMPAIGN) {
        return -1;
      }
      return a.label.localeCompare(b.label);
    });
};

// An "Include" group (arm content) and an "Exclude" group (holdouts), empty groups omitted, each
// sub-grouped by campaign.
const groupedOffers = computed(() => {
  const includeIds = props.offerIds.filter((id) => getOfferMode(id) !== "EXCLUDE");
  const excludeIds = props.offerIds.filter((id) => getOfferMode(id) === "EXCLUDE");

  const groups: {
    label: string;
    mode: string;
    campaigns: ReturnType<typeof groupByCampaign>;
  }[] = [];
  if (includeIds.length > 0) {
    groups.push({ label: "Include", mode: "INCLUDE", campaigns: groupByCampaign(includeIds) });
  }
  if (excludeIds.length > 0) {
    groups.push({ label: "Exclude", mode: "EXCLUDE", campaigns: groupByCampaign(excludeIds) });
  }
  return groups;
});
</script>

<style lang="scss" scoped>
.offer-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.offer-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.offer-group-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &.mode-include {
    color: #15803d;
  }

  &.mode-exclude {
    color: #b91c1c;
  }

  // The icon inherits the header's mode color so it matches the title. :deep + the extra class/scope
  // specificity beats CohortDetails' ancestor `svg { color: blue }` rule that penetrates this child.
  :deep(svg) {
    color: inherit;
  }
}
</style>
